import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import Button from 'components/Button';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import {
  IQBDClassesSearchQuery,
  useFetchQuickbookPaginatedClasses,
} from 'hooks/integrations/useFetchPaginatedQuickbookDesktopClasses';
import { fetchQBTimeeroPaginatedGroups } from 'hooks/integrations/useFetchQBTimeeroPaginatedGroups';
import { IQBClasses, useImportQuickbookClasses } from 'hooks/integrations/useImportQuickbookClasses';
import { IQuickbookDesktopMapClass, useMapQuickbookDesktopClass } from 'hooks/integrations/useMapQuickbookDesktopClass';
import { useUnMapQuickbookDesktopClass } from 'hooks/integrations/useUnMapQuickbookDesktopClass';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

const initialSearchQuery: IQBDClassesSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const ClassMapping: React.FC<unknown> = () => {
  const [mappedClass, setMappedClass] = useState<any>(null);
  const [currentMapClass, setCurrentMapClass] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IQBDClassesSearchQuery>(initialSearchQuery);

  const { t } = useTranslation(['jobsTasks', 'common', 'integrations', 'employees']);

  const { data: qbdClassesData, status: qbdClassesStatus, refetch } = useFetchQuickbookPaginatedClasses(searchQuery);
  const { status: qbdMapClassStatus, mutateAsync: qbdMapClassMutateAsync } = useMapQuickbookDesktopClass();
  const { status: qbdUnMapClassStatus, mutateAsync: qbdUnMapClassMutateAsync } = useUnMapQuickbookDesktopClass();
  const { status: qbdQuickbookImportClassStatus, mutateAsync: qbdQuickbookImportClassMutateAsync } =
    useImportQuickbookClasses();

  // checks for new data every 5 seconds

  useEffect(
    () => {
      const apiFetch = setInterval(() => {
        refetch();
      }, 60000);

      return () => {
        clearInterval(apiFetch);
      };
    },
    // eslint-disable-next-line
    []
  );

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />;
      },
    },
    {
      Header: t('integrations:quickbooksDesktop.quickbooksClasses'),
      accessor: 'name',
      Cell: (data: CellValue) => {
        return (
          <Text textOverflow="ellipsis" overflow="hidden">
            {data?.row?.original?.name}
          </Text>
        );
      },
    },
    {
      Header: t('common:status'),
      accessor: 'status',
      width: 80,
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.active ? 'Active' : 'Inactive'}</Text>;
      },
    },
    {
      Header: t('integrations:mapping'),
      accessor: 'role',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <Box position="relative" width="100%">
            {qbdMapClassStatus === 'loading' && currentMapClass === data.row.original.id && (
              <Flex
                width="100%"
                height="100%"
                position="absolute"
                zIndex={2}
                justifyContent="center"
                alignItems="center"
                background="rgba(255, 255, 255, 0.7)"
              >
                <Spinner size="xs" />
              </Flex>
            )}
            <ReactSelectAsyncPaginate
              loadOptions={groupLoadOptions}
              additional={{
                page: 1,
              }}
              value={
                data.row.original?.selected_group
                  ? data.row.original?.selected_group || null
                  : mappedClass &&
                      mappedClass[`${data.row.original.id}`] &&
                      mappedClass[`${data.row.original.id}`].group
                    ? mappedClass[`${data.row.original.id}`].group
                    : null
              }
              onChange={(value: any) => {
                setMappedClass((prevValue: any) => ({
                  ...prevValue,
                  [data.row.original.id]: {
                    group: value || null,
                  },
                }));
              }}
              size="md"
              variant="filled"
              fontSize="sm"
              width="100%"
              debounceTimeout={300}
              menuPortalTarget={document.body}
              isSearchable
              placeholder={t('employees:selectGroup')}
              getOptionLabel={(option: any) => option.name}
              getOptionValue={(option: any) => option.id}
            />
          </Box>
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      Cell: (data: CellValue) => {
        return (
          <>
            {data.row.original?.selected_group ? (
              <Button
                _hover={{ bg: 'secondary.100' }}
                fontWeight="bold"
                background="rgba(250, 85, 85, 0.2)"
                color="rgba(250, 85, 85, 1)"
                isLoading={qbdUnMapClassStatus === 'loading' && currentMapClass === data.row.original.id}
                onClick={() => onUnMapClass(data.row.original.id, data.row.original.selected_group.id)}
              >
                {t('integrations:removeMapping')}
              </Button>
            ) : (
              <Button
                _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
                background="rgba(255, 103, 32, 0.1)"
                colorScheme="rgba(255, 103, 32, 0.3)"
                color="secondary.500"
                isLoading={qbdMapClassStatus === 'loading' && currentMapClass === data.row.original.id}
                onClick={() => onMapClass(data.row.original.id)}
              >
                {t('integrations:mapClass')}
              </Button>
            )}
          </>
        );
      },
    },
  ];

  const onMapClass = (quickbookId: number) => {
    setCurrentMapClass(quickbookId);
    const postData: IQuickbookDesktopMapClass = {
      group_id: mappedClass[quickbookId]?.group?.id || null,
      quickbook_class_id: quickbookId,
    };
    qbdMapClassMutateAsync(postData).then(() => {
      refetch();
      setMappedClass(null);
    });
  };

  const onUnMapClass = (quickbookId: number, groupId: number) => {
    setCurrentMapClass(quickbookId);
    const postData: IQuickbookDesktopMapClass = {
      group_id: groupId,
      quickbook_class_id: quickbookId,
    };
    qbdUnMapClassMutateAsync(postData).then(() => {
      refetch();
      setMappedClass(null);
    });
  };

  const onImportClasses = (selectedIds: Array<number>) => {
    const postData: IQBClasses = {
      quick_book_class_ids: selectedIds,
    };
    qbdQuickbookImportClassMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const groupLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchQBTimeeroPaginatedGroups, { page, search });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IQBDClassesSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <Box>
      <Title>{t('integrations:quickbooksDesktop.desktopMapping')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: t('integrations:quickbooksDesktop.searchQuickbooksClasses'),
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <Table
        columns={columns}
        data={qbdClassesData?.payload || []}
        isLoading={qbdClassesStatus === 'loading'}
        enableRowSelect
        paginationData={qbdClassesData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        renderTableFooter={(selectedFlatRows: any) => {
          const selected = selectedFlatRows.map((item: any) => item.original.id);
          return (
            <Flex
              width="100%"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
              border="1px"
              borderColor="primary.100"
              boxShadow="0 -4px 4px -1px rgba(0, 0, 0, 0.1),0 -2px 2px -1px rgba(0, 0, 0, 0.06);"
              px="6"
              py="4"
            >
              <Text fontWeight="semibold">
                {selectedFlatRows.length} {t('common:selected')}
              </Text>
              <Box>
                <Button
                  colorScheme="primary"
                  isLoading={qbdQuickbookImportClassStatus === 'loading'}
                  onClick={() => onImportClasses(selected)}
                >
                  {t('integrations:quickbooksDesktop.importClasses')}
                </Button>
              </Box>
            </Flex>
          );
        }}
      />
    </Box>
  );
};
export default ClassMapping;
